import ClientBookingRequests from './ClientBookingRequests';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  PlusCircle,
  Activity,
} from "lucide-react";
import SalesPipelineSummary from "./SalesPipelineSummary";
import AddPipeline from "./AddPipeline";
import { graphqlRequest } from "../utils/graphqlRequest";
import "../styles/SalesDashboard.css";

const PIPELINE_OVERVIEW_QUERY = `
  query FindAllPipelineOverview {
    findAllPipelineOverview {
      id
      name
      totalDeals
    }
  }
`;

const LEAD_DASHBOARD_QUERY = `
  query GetLeadDashboard {
    getLeadDashboard {
      totalLeads
      newLeads
      convertedLeads
      lostLeads
      revenuePotential
    }
  }
`;

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "$0";
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(n);
}

export default function SalesDashboard() {
  const [showForm, setShowForm] = useState(false);
  const [summaryKey, setSummaryKey] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pipelineCount, setPipelineCount] = useState(0);
  const [openDeals, setOpenDeals] = useState(0);
  const [leadStats, setLeadStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    convertedLeads: 0,
    lostLeads: 0,
    revenuePotential: 0,
  });

  const fetchStats = useCallback(async (background = false) => {
    if (!background) setStatsLoading(true);
    setStatsError("");
    try {
      const [pipelineRes, leadRes] = await Promise.allSettled([
        graphqlRequest(PIPELINE_OVERVIEW_QUERY),
        graphqlRequest(LEAD_DASHBOARD_QUERY),
      ]);

      if (pipelineRes.status === "fulfilled") {
        const rows = pipelineRes.value?.findAllPipelineOverview || [];
        setPipelineCount(rows.length);
        setOpenDeals(
          rows.reduce((sum, p) => sum + (Number(p.totalDeals) || 0), 0),
        );
      }

      if (leadRes.status === "fulfilled") {
        const d = leadRes.value?.getLeadDashboard || {};
        setLeadStats({
          totalLeads: Number(d.totalLeads) || 0,
          newLeads: Number(d.newLeads) || 0,
          convertedLeads: Number(d.convertedLeads) || 0,
          lostLeads: Number(d.lostLeads) || 0,
          revenuePotential: Number(d.revenuePotential) || 0,
        });
      }

      if (pipelineRes.status === "rejected" && leadRes.status === "rejected") {
        throw new Error("Pipeline dashboard data could not be loaded");
      }
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Pipeline overview stats error:", err);
      setStatsError(err?.message || "Pipeline dashboard data could not be loaded");
    } finally {
      if (!background) setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const refresh = () => fetchStats(true);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 30000);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [fetchStats, summaryKey]);

  const handleAddPipeline = () => {
    setSummaryKey((k) => k + 1);
    setShowForm(false);
  };

  const conversionRate = useMemo(() => {
    const decided = leadStats.convertedLeads + leadStats.lostLeads;
    return decided > 0
      ? Math.round((leadStats.convertedLeads / decided) * 100)
      : 0;
  }, [leadStats.convertedLeads, leadStats.lostLeads]);

  const stats = useMemo(
    () => [
      {
        label: "Open opportunities",
        value: statsLoading ? "…" : String(openDeals),
        icon: <BarChart3 className="icon blue" />,
      },
      {
        label: "Conversion Rate",
        value: statsLoading ? "…" : `${conversionRate}%`,
        icon: <TrendingUp className="icon green" />,
        detail: `${leadStats.convertedLeads} won · ${leadStats.lostLeads} lost`,
      },
      {
        label: "Leads in CRM",
        value: statsLoading ? "…" : String(leadStats.totalLeads),
        icon: <Target className="icon purple" />,
      },
      {
        label: "Lead value captured",
        value: statsLoading ? "…" : formatMoney(leadStats.revenuePotential),
        icon: <DollarSign className="icon yellow" />,
      },
      {
        label: "New Leads",
        value: statsLoading ? "…" : String(leadStats.newLeads),
        icon: <Users className="icon pink" />,
      },
      {
        label: "Active Pipelines",
        value: statsLoading ? "…" : String(pipelineCount),
        icon: <Activity className="icon red" />,
      },
    ],
    [statsLoading, openDeals, leadStats, conversionRate, pipelineCount],
  );

  return (
    <div className="dashboard-container">
      <ClientBookingRequests />
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Pipeline Overview</h1>
          <p className="dashboard-subtitle">
            Manage qualified opportunities from assessment to closed won.
          </p>
        </div>
        <div className="pipeline-header-actions">
          <button className="add-pipeline-btn" onClick={() => setShowForm(true)}>
            <PlusCircle className="plus-icon" /> New pipeline
          </button>
        </div>
      </div>

      <div className="pipeline-dashboard-meta" aria-live="polite">
        {statsError ? (
          <span className="pipeline-dashboard-error">{statsError}</span>
        ) : lastUpdated ? (
          <span>
            Updated{" "}
            {lastUpdated.toLocaleTimeString("en-AU", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        ) : null}
      </div>

      {/* Dashboard Boxes */}
      <div className="dashboard-grid-sales">
        {stats.map((item, index) => (
          <div className="dashboard-box-sales" key={index}>
            {item.icon}
            <div>
              <p className="label-sales">{item.label}</p>
              <h2 className="value-sales">{item.value}</h2>
              {item.detail ? <p className="metric-detail">{item.detail}</p> : null}
            </div>
          </div>
        ))}
      </div>

      {/* Compact Sales Pipeline Section */}
      <div className="sales-pipeline-section">
        <h2 className="section-title">Sales pipelines</h2>
        <SalesPipelineSummary
          key={summaryKey}
          onRefreshDashboard={fetchStats}
        />
      </div>

      {showForm && (
        <AddPipeline
          onClose={() => {
            setShowForm(false);
            setSummaryKey((k) => k + 1);
          }}
          onSave={handleAddPipeline}
        />
      )}
    </div>
  );
}
